/**
 * useIndustryWorkOrders Hook
 * CRUD operations for industry_work_orders table (Auto Repair)
 * Automatically filters by company_id
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { CompanyType } from '@/types/company';
import type { IndustryWorkOrder } from '@/types/industry';
import { useToast } from '@/components/ui/use-toast';

interface UseIndustryWorkOrdersOptions {
  domain?: CompanyType;
  status?: IndustryWorkOrder['status'] | IndustryWorkOrder['status'][];
  client_id?: number;
  vehicle_id?: string;
}

type WorkOrderStatus = 'open' | 'in_progress' | 'waiting_parts' | 'completed' | 'cancelled';

interface CreateWorkOrderInput {
  order_number: string;
  client_id: number;
  vehicle_id?: string;
  status?: WorkOrderStatus;
  labor_cost?: number;
  parts_cost?: number;
  total_cost?: number;
  opened_date: string;
  estimated_completion?: string;
  completed_date?: string;
  description: string;
  diagnosis?: string;
  work_performed?: string;
  metadata?: Record<string, any>;
}

interface UpdateWorkOrderInput extends Partial<CreateWorkOrderInput> {
  id: string;
}

export function useIndustryWorkOrders(options: UseIndustryWorkOrdersOptions = {}) {
  const { status, client_id, vehicle_id } = options;
  const queryClient = useQueryClient();
  const { toast } = useToast();

  // Get current company
  const { data: company } = useQuery({
    queryKey: ['current_company'],
    queryFn: async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('companies')
        .select('*')
        .eq('user_id', user.id)
        .single();

      if (error) throw error;
      return data;
    },
  });

  // Fetch work orders
  const {
    data: workOrders,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: ['industry_work_orders', company?.id, status, client_id, vehicle_id],
    queryFn: async () => {
      if (!company) return [];

      let query = supabase
        .from('industry_work_orders')
        .select('*')
        .eq('company_id', company.id)
        .order('created_at', { ascending: false });

      if (status) {
        if (Array.isArray(status)) {
          query = query.in('status', status);
        } else {
          query = query.eq('status', status);
        }
      }

      if (client_id) {
        query = query.eq('client_id', client_id);
      }

      if (vehicle_id) {
        query = query.eq('vehicle_id', vehicle_id);
      }

      const { data, error } = await query;

      if (error) throw error;
      return data as IndustryWorkOrder[];
    },
    enabled: !!company,
  });

  // Create work order
  const createWorkOrder = useMutation({
    mutationFn: async (input: CreateWorkOrderInput) => {
      if (!company) throw new Error('No company found');

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const { data, error } = await supabase
        .from('industry_work_orders')
        .insert({
          ...input,
          company_id: company.id,
          user_id: user.id,
          labor_cost: input.labor_cost || 0,
          parts_cost: input.parts_cost || 0,
          total_cost: input.total_cost || (input.labor_cost || 0) + (input.parts_cost || 0),
          metadata: input.metadata || {},
        })
        .select()
        .single();

      if (error) throw error;
      return data as IndustryWorkOrder;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_work_orders', company?.id],
      });
      toast({
        title: 'Ordem de serviço criada',
        description: `OS ${data.order_number} foi criada com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao criar ordem de serviço',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Update work order
  const updateWorkOrder = useMutation({
    mutationFn: async (input: UpdateWorkOrderInput) => {
      const { id, ...updates } = input;

      // Recalculate total if labor or parts cost is being updated
      if (updates.labor_cost !== undefined || updates.parts_cost !== undefined) {
        const { data: current } = await supabase
          .from('industry_work_orders')
          .select('labor_cost, parts_cost')
          .eq('id', id)
          .single();

        if (current) {
          const newLaborCost = updates.labor_cost !== undefined ? updates.labor_cost : current.labor_cost;
          const newPartsCost = updates.parts_cost !== undefined ? updates.parts_cost : current.parts_cost;
          updates.total_cost = newLaborCost + newPartsCost;
        }
      }

      const { data, error } = await supabase
        .from('industry_work_orders')
        .update(updates)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data as IndustryWorkOrder;
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({
        queryKey: ['industry_work_orders', company?.id],
      });
      toast({
        title: 'Ordem de serviço atualizada',
        description: `OS ${data.order_number} foi atualizada com sucesso.`,
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao atualizar ordem de serviço',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  // Delete work order
  const deleteWorkOrder = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase
        .from('industry_work_orders')
        .delete()
        .eq('id', id);

      if (error) throw error;
      return id;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['industry_work_orders', company?.id],
      });
      toast({
        title: 'Ordem de serviço excluída',
        description: 'A ordem de serviço foi excluída com sucesso.',
      });
    },
    onError: (error) => {
      toast({
        title: 'Erro ao excluir ordem de serviço',
        description: error.message,
        variant: 'destructive',
      });
    },
  });

  return {
    workOrders: workOrders || [],
    isLoading,
    error,
    refetch,
    createWorkOrder: createWorkOrder.mutate,
    createWorkOrderAsync: createWorkOrder.mutateAsync,
    updateWorkOrder: updateWorkOrder.mutate,
    updateWorkOrderAsync: updateWorkOrder.mutateAsync,
    deleteWorkOrder: deleteWorkOrder.mutate,
    deleteWorkOrderAsync: deleteWorkOrder.mutateAsync,
    isCreating: createWorkOrder.isPending,
    isUpdating: updateWorkOrder.isPending,
    isDeleting: deleteWorkOrder.isPending,
  };
}
